import { TrackEnum } from "@selector/shared/openapi";
import { type PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";

import { getFeatureTypes } from "../../api/fetch.ts";
import { syntenyViewerDefaultTracks } from "../../settings.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import type { TrackFeatures, TrackType } from "../../types.ts";
import { TrackConfigurationContext } from "./TrackConfigurationContext.ts";

interface TrackConfigurationProviderProps extends PropsWithChildren {
  isOpen: boolean;
}

export const TrackConfigurationProvider = ({ children, isOpen }: TrackConfigurationProviderProps) => {
  const getTrackConfiguration = useGenomeViewerStore((store) => store.getTrackConfiguration);
  const [trackConfiguration, setTrackConfiguration] = useState(new Map<string, TrackFeatures>(getTrackConfiguration()));
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const removeTrackGroups = useGenomeViewerStore((store) => store.removeTrackGroups);
  const reorderTrackGroups = useGenomeViewerStore((state) => state.reorderTrackGroupsById);
  const updateTrackConfiguration = useGenomeViewerStore((store) => store.applyTrackConfiguration);
  const [groupsToDelete, setGroupsToDelete] = useState<string[]>([]);
  const { showBoundary } = useErrorBoundary();

  const [availableTrackConfiguration, setAvailableTrackConfiguration] = useState(new Map<string, TrackFeatures>());
  const [isLoading, setIsLoading] = useState(isOpen);

  const trackGroups = useGenomeViewerStore((store) => store.trackGroups);

  const defaultTrackTypes: TrackType[] = useMemo(() => syntenyViewerDefaultTracks.map((track) => track.type), []);

  // provider is mounted when dialog opens.
  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;

    const fetchTrackConfiguration = async () => {
      const configuration = await Promise.all(
        Array.from(trackGroups.entries()).map(async ([groupId, trackGroup]) => {
          const trackFeatures: TrackFeatures = {
            defaultTypes: [...defaultTrackTypes],
            nuFeatureTypes: [],
          };

          try {
            const featureTypes = await getFeatureTypes(trackGroup.organismId);
            const hasNuFeatures = featureTypes.nuFeatureTypes.length > 0;

            if (hasNuFeatures) {
              trackFeatures.defaultTypes = [...trackFeatures.defaultTypes, TrackEnum.NUCLEOTIDE_FEATURE];
            }
            trackFeatures.nuFeatureTypes = featureTypes.nuFeatureTypes;
          } catch (error) {
            showBoundary(error);
          }

          return [groupId, trackFeatures] as const;
        }),
      );

      if (!isCancelled) {
        setAvailableTrackConfiguration(new Map(configuration));
        setIsLoading(false);
      }
    };

    fetchTrackConfiguration();

    return () => {
      isCancelled = true;
    };
  }, [defaultTrackTypes, isOpen, showBoundary, trackGroups]);

  const applyChanges = () => {
    // handle delete groups
    removeTrackGroups(groupsToDelete);

    for (const id of groupsToDelete) {
      trackConfiguration.delete(id);
    }

    // handle reordering
    reorderTrackGroups([...trackConfiguration.entries()].map(([groupId, _]) => groupId));

    updateTrackConfiguration(getTrackConfiguration(), trackConfiguration, showBoundary);

    setGroupsToDelete([]);

    // To update the selected features if there are some, when adding/removing tracks
    useGenomeViewerStore.setState({ needsRefresh: true });
  };

  const value = {
    trackConfiguration,
    setTrackConfiguration,
    selectedGroupIds,
    setSelectedGroupIds,
    applyChanges,
    setGroupsToDelete,
    groupsToDelete,
    availableTrackConfiguration,
    isLoading,
  };

  return <TrackConfigurationContext.Provider value={value}>{children}</TrackConfigurationContext.Provider>;
};
